<template>
  <div class="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700 px-2 pt-2">
    <Button
      v-for="tab in tabs"
      :key="tab.id"
      :variant="activeTab === tab.id ? 'primary' : 'ghost'"
      size="sm"
      @click="handleTabChange(tab.id)"
      class="rounded-b-none"
      :disabled="tab.disabled"
    >
      <component :is="tab.icon" class="w-4 h-4 mr-1.5" />
      {{ tab.label }}
      <Badge v-if="tab.disabled" variant="warning" size="sm" class="ml-1.5">Soon</Badge>
    </Button>
  </div>
</template>

<script setup lang="ts">
  import { computed, h } from 'vue';
  import { Button, Badge } from '@asabdev/asab-ui';
  import type { PlaygroundTab } from '../../types/session';

  // Tab icons as functional components
  const TextIcon = () =>
    h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M4 6h16M4 12h16M4 18h7',
      }),
    ]);

  const VisionIcon = () =>
    h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      }),
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
      }),
    ]);

  const AudioIcon = () =>
    h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z',
      }),
    ]);

  const EmbeddingsIcon = () =>
    h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
      }),
    ]);

  const ModerationIcon = () =>
    h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      }),
    ]);

  const VideoIcon = () =>
    h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
      }),
    ]);

  // Props
  interface Props {
    activeTab: PlaygroundTab;
  }

  defineProps<Props>();

  // Emits
  interface Emits {
    (e: 'tabChange', tab: PlaygroundTab): void;
  }

  const emit = defineEmits<Emits>();

  // Tab definitions
  const tabs = computed(() => [
    { id: 'text' as PlaygroundTab, label: 'Text', icon: TextIcon, disabled: false },
    { id: 'vision' as PlaygroundTab, label: 'Image', icon: VisionIcon, disabled: false },
    { id: 'audio' as PlaygroundTab, label: 'Audio', icon: AudioIcon, disabled: false },
    {
      id: 'embeddings' as PlaygroundTab,
      label: 'Embeddings',
      icon: EmbeddingsIcon,
      disabled: false,
    },
    {
      id: 'moderation' as PlaygroundTab,
      label: 'Moderation',
      icon: ModerationIcon,
      disabled: false,
    },
    { id: 'video' as PlaygroundTab, label: 'Video', icon: VideoIcon, disabled: true },
  ]);

  // Methods
  const handleTabChange = (tab: PlaygroundTab) => {
    emit('tabChange', tab);
  };
</script>
